import type { VariantProps } from 'class-variance-authority'
import { formatDistanceToNow } from 'date-fns'

import {
  WorkflowExecutionStatus,
  WorkflowExecutionStepStatus,
  type WorkflowExecution,
} from '@/gen/cortado/v1/cortado_pb'
import { badgeVariants } from '@/components/ui/badge'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

export function workflowExecutionStatusLabel(status: WorkflowExecutionStatus): string {
  switch (status) {
    case WorkflowExecutionStatus.PENDING:
      return 'Pending'
    case WorkflowExecutionStatus.RUNNING:
      return 'Running'
    case WorkflowExecutionStatus.SUCCEEDED:
      return 'Succeeded'
    case WorkflowExecutionStatus.FAILED:
      return 'Failed'
    case WorkflowExecutionStatus.CANCELLED:
      return 'Cancelled'
    case WorkflowExecutionStatus.CONDITION_UNMET:
      return 'Condition unmet'
    case WorkflowExecutionStatus.STOPPED:
      return 'Stopped'
    case WorkflowExecutionStatus.UNSPECIFIED:
    default:
      return 'Unknown'
  }
}

export function workflowExecutionStatusVariant(status: WorkflowExecutionStatus): BadgeVariant {
  switch (status) {
    case WorkflowExecutionStatus.SUCCEEDED:
      return 'success'
    case WorkflowExecutionStatus.FAILED:
      return 'destructive'
    case WorkflowExecutionStatus.CONDITION_UNMET:
      return 'warning'
    case WorkflowExecutionStatus.RUNNING:
      return 'info'
    case WorkflowExecutionStatus.PENDING:
      return 'secondary'
    case WorkflowExecutionStatus.CANCELLED:
      return 'outline'
    // A stopped run ended intentionally (a stop condition matched), so it gets
    // its own distinct color rather than reusing the red FAILED / yellow
    // CONDITION_UNMET treatments.
    case WorkflowExecutionStatus.STOPPED:
      return 'purple'
    case WorkflowExecutionStatus.UNSPECIFIED:
    default:
      return 'outline'
  }
}

export function workflowExecutionStepStatusLabel(status: WorkflowExecutionStepStatus): string {
  switch (status) {
    case WorkflowExecutionStepStatus.PENDING:
      return 'Pending'
    case WorkflowExecutionStepStatus.RUNNING:
      return 'Running'
    case WorkflowExecutionStepStatus.SUCCEEDED:
      return 'Succeeded'
    case WorkflowExecutionStepStatus.FAILED:
      return 'Failed'
    case WorkflowExecutionStepStatus.SKIPPED:
      return 'Skipped'
    case WorkflowExecutionStepStatus.UNSPECIFIED:
    default:
      return 'Unknown'
  }
}

export function workflowExecutionStepStatusVariant(status: WorkflowExecutionStepStatus): BadgeVariant {
  switch (status) {
    case WorkflowExecutionStepStatus.SUCCEEDED:
      return 'success'
    case WorkflowExecutionStepStatus.FAILED:
      return 'destructive'
    case WorkflowExecutionStepStatus.RUNNING:
      return 'info'
    case WorkflowExecutionStepStatus.PENDING:
      return 'secondary'
    case WorkflowExecutionStepStatus.SKIPPED:
    case WorkflowExecutionStepStatus.UNSPECIFIED:
    default:
      return 'outline'
  }
}

/**
 * Human labels for the canonical dot-separated lifecycle event strings the
 * backend stores in `stopped_by_event_type` (e.g. "quote.accepted"). These
 * mirror the labels in EVENT_METADATA, but `stopped_by_event_type` is a raw
 * string field — not the numeric WorkflowTriggerEventType enum — so it needs
 * its own map.
 */
const STOP_EVENT_TYPE_LABELS: Record<string, string> = {
  'inquiry.created': 'Lead created',
  'inquiry.stage_changed': 'Lead stage changed',
  'inquiry.assigned': 'Lead assigned',
  'quote.created': 'Quote created',
  'quote.sent': 'Quote sent',
  'quote.viewed': 'Quote viewed',
  'quote.accepted': 'Quote accepted',
  'quote.rejected': 'Quote rejected',
  'quote.expired': 'Quote expired',
  'invoice.sent': 'Invoice sent',
  'invoice.paid': 'Invoice paid',
  'invoice.overdue': 'Invoice overdue',
  'invoice.payment_due_soon': 'Payment due soon',
  'payment.received': 'Payment received',
  'payment.failed': 'Payment failed',
  'event.created': 'Event created',
  'event.completed': 'Event completed',
  'event.canceled': 'Event canceled',
  'event.starting_soon': 'Event starting soon',
  'contact.created': 'Contact created',
  'booking.created': 'Booking created',
  'booking.stage_changed': 'Booking stage changed',
  // "Stopped: new message" — the customer wrote back.
  'inquiry.email_received': 'New message',
  'booking.email_received': 'New message',
  'contact.email_received': 'New message',
  'inquiry.no_reply_for': 'No reply',
}

/**
 * Maps a raw `stopped_by_event_type` string to a human label for the runs UI's
 * "Stopped by: <event>" detail. Unknown/future event strings fall back to a
 * humanized form ("some.new_event" → "Some new event"). Returns undefined for
 * an empty value so callers can skip rendering the detail.
 */
export function stoppedByEventTypeLabel(eventType: string | undefined): string | undefined {
  if (!eventType) return undefined
  const known = STOP_EVENT_TYPE_LABELS[eventType]
  if (known) return known
  return eventType
    .replace(/[._]+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Derives a human "waiting" label for an active (RUNNING) execution whose
 * current step is an in-flight WAIT. `waitingUntil` is a derived backend field
 * (step started_at + the WAIT's configured duration). Returns undefined when
 * the run is not running or has no derived waiting time — callers fall back to
 * the status badge / current step label.
 *
 * @example "Waiting — sends ~in 2 days"
 */
export function workflowExecutionWaitingLabel(
  execution: WorkflowExecution
): string | undefined {
  if (execution.status !== WorkflowExecutionStatus.RUNNING) return undefined
  if (!execution.waitingUntil) return undefined

  const seconds =
    typeof execution.waitingUntil.seconds === 'bigint'
      ? Number(execution.waitingUntil.seconds)
      : (execution.waitingUntil.seconds as number)
  if (seconds == null || Number.isNaN(seconds)) return undefined

  const target = new Date(seconds * 1000)
  if (Number.isNaN(target.getTime())) return undefined

  // The ETA is derived (step started_at + WAIT duration), not polled from
  // Temporal, so it can already be in the past when the WAIT has elapsed but the
  // step hasn't advanced yet (or under minor clock skew). Avoid the
  // contradictory "sends ~2 minutes ago".
  if (target.getTime() <= Date.now()) return 'Waiting — finishing up'

  // addSuffix renders future times as "in 2 days"; the "~" signals the ETA is
  // approximate (derived, not polled from Temporal).
  return `Waiting — sends ~${formatDistanceToNow(target, { addSuffix: true })}`
}
